import * as React from "react";
import {FormattedMessage} from "react-intl";
import {connect, useDispatch} from "react-redux";
import {Link, useNavigate} from "react-router-dom";
import {AnyAction, bindActionCreators, Dispatch} from "redux";
import {
    clientReportingEnabled,
    cprPortfolioOverviewEnabled,
    hasAonTrustCompany,
    hasClientPortfolioAccess,
    hasClientReports, hasClientResearchEnabled,
    hasCprSharedMediaAdmin,
    hasPortfolioAccess,
    hasPowerbiReportAdmin,
    hasResearchAccess,
    insightsEnabled,
    isEditingPlanPortfolio,
    oddIqEnabled,
    oddReportsEnabled,
    performanceReportingEnabled,
    sharedDocumentsEnabled,
} from "../../../../mainReducerMapSelectors";
import {getBrowserLocationPath} from "../../../api/BrowserWrapper";
import {isIE} from "../../../utils/browserUtil";
import {AllNavigationMenus, INavigationMenu, NavigationTab,} from "../../../utils/navigationUtils";
import {
    AON_TRUST_PLAN,
    EMPOWER_RESULTS_PLAN,
    hasConsultantLevelAccess,
    hasPortfolioPageAccess, hasResearchPageAccess
} from "../../../utils/sessionUtil";
import {showMasterLectureSeries} from "../../../utils/planUtil";
import {ISessionState} from "../../session/SessionReducer";
import {Menu} from "../Menu.component";
import PlanList from "../PlanList.component";
import HeaderActions from "./HeaderActions";
import {handleEnter} from "../../../utils/commonUtil";
import {ICorsErrorModalOpen} from "../OktaLogout";
import {IApplicationRootState} from "../../../../applicationState";
import {HeaderEditPlanName} from "./HeaderEditPlanName";
import {useEffect, useState} from "react";
import {AdminActionTypes} from "../../research/edit-portfolio/editPortfolioActions";
import {navigateTo} from "../../../../navigateTo";
import {AnyThunkDispatch} from "../../../../commonTypes";
import {useClickOutside} from "../../../utils/UseClickOutside";

export type IHeaderPropsFromState = ISessionState & {
    hasResearchAccess: boolean;
    showUsernameMenu: boolean;
    currentNavigationTab?: NavigationTab;
    editingPlanPortfolio: boolean;
    sharedDocumentsEnabled: boolean;
    oddIqEnabled: boolean;
    oddReportsEnabled: boolean;
    clientReportingEnabled: boolean;
    hasClientReports: boolean;
    performanceReportingEnabled: boolean;
    cprPortfolioOverviewEnabled: boolean;
    insightsEnabled: boolean;
    powerbiReportAdmin: boolean;
    cprMediaAdmin: boolean;
    aonTrustCompany: boolean;
    hasPortfolioAccess: boolean;
    clientPortfolioEnabled: boolean;
    hasClientResearchEnabled: boolean;
};

export const ATC_HEADER_LOGO_TEXT = "Aon Collective Investment Trust";

export type IHeaderProps = IHeaderPropsFromState & IHeaderPropsFromActions & ICorsErrorModalOpen;

export const Header: React.FunctionComponent<IHeaderProps> = (props) => {
    const navigateHook = useNavigate();
    const dispatch: AnyThunkDispatch = useDispatch();
    const [navOpen, setNavOpen] = useState(false);

    function currentTabName(hasPortfolioAccess: boolean): string | undefined {
        const excludeMenu = hasPortfolioAccess ? NavigationTab.MANAGER_RESEARCH : NavigationTab.PORTFOLIO;
        const navigationMenu = AllNavigationMenus
            .filter((navMenu: INavigationMenu) => navMenu.id !== excludeMenu.toString())
            .find((navMenu: INavigationMenu) => browserLocationMatchesPath(navMenu));

        return (navigationMenu) ? navigationMenu.id : undefined;
    }

    function browserLocationMatchesPath(navMenu: INavigationMenu) {
        const currentUri = getBrowserLocationPath().split("/")[1];
        return navMenu.paths.some((path) => currentUri === path);
    }

    useEffect(() => {
        if (props.actions) {
            props.actions.setMenuVisibility(false);
        }
    }, []);

    useEffect(() => {
        selectNavigationTab(currentTabName(props.hasPortfolioAccess));
    }, [props]);

    const closeMenu = () => props.actions!.setMenuVisibility(false);
    const {targetRef: userMenuRef} = useClickOutside(closeMenu);

    return <header id="header-main" className={getHeaderClasses()} data-testid="header-main">
                <div className="header__main--hexagons"/>
                <div className="header__content max-width-1600">
                    <Link to="/home" id="aon-logo" tabIndex={0}
                          onClick={() => props.actions!.clearCurrentNavigationTab()}>
                        {renderHeaderText(props.aonTrustCompany)}
                    </Link>
                    {!props.aonTrustCompany && (
                        <button
                            className="hamburger-toggle"
                            onClick={() => setNavOpen(!navOpen)}
                            aria-label="Toggle navigation"
                            aria-expanded={navOpen}
                            data-testid="hamburger-toggle"
                        >
                            {navOpen
                                ? <i className="fa-regular fa-x" aria-hidden="true"/>
                                : <i className="fa-regular fa-bars" aria-hidden="true"/>
                            }
                        </button>
                    )}
                    {!props.aonTrustCompany && renderTabs()}
                    <div className="header__right">
                        <div>
                            <div className="header-bar__menu-items">
                                {hasCurrentPlanEligibleForNameChange()
                                    && <HeaderEditPlanName {...{
                                        currentPlan: props.currentPlan!,
                                        planList: props.plans
                                    }}/>
                                }
                                <PlanList {...{
                                    planList: props.plans,
                                    currentPlan: props.currentPlan!,
                                }} />
                                <div className="user-menu__wrapper" ref={userMenuRef}>
                                    <button
                                        className="header-bar__user-icon"
                                        onClick={() => toggleUsernameMenu()}
                                        aria-label="User menu"
                                        aria-haspopup="true"
                                        aria-expanded={props.showUsernameMenu}
                                    >
                                        <i className="fa-light fa-user" aria-hidden="true" />
                                    </button>
                                    <Menu username={props.username}
                                          shown={props.showUsernameMenu}
                                          closeMenuFn={closeMenu}
                                          userType={props.userType}
                                          hasRaAccess={props.hasRaAccess!}
                                          setCorsErrorModalOpen={props.setCorsErrorModalOpen}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>;


    function renderTabs() {
        const style = isIE() ? {minWidth: 700} : {};
        return <div className={`header__left${navOpen ? " nav-open" : ""}`} style={style} data-testid="header__left">
            <div className="navigation-tabs">
                {getTabElements()}
            </div>
        </div>;
    }

    function renderHeaderText(aonTrustCompany: boolean) {
        return aonTrustCompany
        ? <div className="header__atc-logo-text">{ATC_HEADER_LOGO_TEXT}</div>
        : <div className="header__aon-logo-text">Aon Iris</div>;
    }


    function getTabElements() {
        const navigate = (path: any, value: any) => {
            if (value !== NavigationTab.MANAGER_RESEARCH && props.editingPlanPortfolio) {
                dispatch({type: AdminActionTypes.END_EDIT_PORTFOLIO});
            }
            navigateTo(path, navigateHook);
            props.actions!.setCurrentNavigationTab(value);
            setNavOpen(false);
        };

        return AllNavigationMenus
            .filter(hasPortfolioAccess)
            .filter(hasResearchAccess)
            .filter(hasSharedDocumentsAccess)
            .filter(hasInsightsAccess)
            .filter(hasMasterLectureSeriesAccess)
            .filter(hasMasterLectureSeriesAccess)
            .filter(hasOddIqAccess)
            .filter(hasOddReportsAccess)
            .filter(hasClientReportingAccess)
            .filter(hasPowerbiReportAdminAccess)
            .filter(hasCprSharedMediaAdminAccess)
            .map(({id, label, elementId, path}) => {
                const className =
                    `navigation-tab old-anchor clickable${(props.currentNavigationTab?.toString() === id) ? " selected" : ""}`;
                    return <a key={elementId}
                              id={elementId}
                              tabIndex={0}
                              onClick={() => navigate(path, id)}
                              onKeyUp={handleEnter(() => navigate(path, id))}
                              className={className}>
                        <FormattedMessage id={`header.${id}`}
                                          defaultMessage={label}/>
                    </a>;
                },
            );
    }

    function hasPortfolioAccess (tab: INavigationMenu)  {
        return hasPortfolioPageAccess(props.hasPortfolioAccess, props.clientPortfolioEnabled)
            || tab.id !== NavigationTab.PORTFOLIO.toString();
    }

    function hasResearchAccess (tab: INavigationMenu) {
       return hasResearchPageAccess(
            props.hasResearchAccess, props.hasPortfolioAccess, props.clientPortfolioEnabled)
        || tab.id !== NavigationTab.MANAGER_RESEARCH.toString();
    }

    function hasSharedDocumentsAccess (tab: INavigationMenu) {
        return (props.sharedDocumentsEnabled && !props.aonTrustCompany)
            || tab.id !== NavigationTab.SHARED_DOCUMENTS.toString();
    }

    function hasPowerbiReportAdminAccess (tab: INavigationMenu) {
        return props.powerbiReportAdmin && hasConsultantLevelAccess()
            || tab.id !== NavigationTab.POWERBI_REPORT_ADMIN.toString();
    }

    function hasCprSharedMediaAdminAccess (tab: INavigationMenu) {
        return props.cprMediaAdmin && hasConsultantLevelAccess()
            || tab.id !== NavigationTab.CPR_SHARED_MEDIA_ADMIN.toString();
    }

    function hasInsightsAccess (tab: INavigationMenu) {
        return props.insightsEnabled || tab.id !== NavigationTab.INSIGHTS.toString();
    }

    function hasMasterLectureSeriesAccess (tab: INavigationMenu) {
        return showMasterLectureSeries() || tab.id !== NavigationTab.MASTER_LECTURE_SERIES.toString();
    }

    function hasOddIqAccess (tab: INavigationMenu) {
        return props.oddIqEnabled || tab.id !== NavigationTab.ODD_IQ.toString();
    }


    function hasClientReportingAccess(tab: INavigationMenu) {
        return ((props.clientReportingEnabled || hasConsultantLevelAccess())
            && (props.performanceReportingEnabled || props.hasClientReports))
            || tab.id !== NavigationTab.CLIENT_REPORTING.toString();
    }

    function hasOddReportsAccess(tab: INavigationMenu) {
        return props.oddReportsEnabled || tab.id !== NavigationTab.ODD_REPORTS.toString();
    }

    function getHeaderClasses() {
        return `main__header 
            ${props.editingPlanPortfolio && "header__edit-portfolio-mode"} 
            ${props.aonTrustCompany && "header__atc-plan"}`;
    }

    function selectNavigationTab(tabName: string | undefined) {
        if (tabName) {
            props.actions!.setCurrentNavigationTab(tabName);
        } else {
            props.actions!.clearCurrentNavigationTab();
        }
    }

    function toggleUsernameMenu() {
        props.actions!.setMenuVisibility(!props.showUsernameMenu);
    }

    function hasCurrentPlanEligibleForNameChange(): boolean {
        return hasConsultantLevelAccess() && props.currentPlan!.name !== AON_TRUST_PLAN &&
            props.currentPlan!.name !== EMPOWER_RESULTS_PLAN;
    }
};

export const mapStateToProps = (state: IApplicationRootState): IHeaderPropsFromState => {
    return {
        ...state.headerRootState!,
        ...state.session!,
        hasResearchAccess: hasResearchAccess(state),
        editingPlanPortfolio: isEditingPlanPortfolio(state),
        sharedDocumentsEnabled: sharedDocumentsEnabled(state),
        oddIqEnabled: oddIqEnabled(state),
        oddReportsEnabled: oddReportsEnabled(state),
        clientReportingEnabled: clientReportingEnabled(state),
        hasClientReports: hasClientReports(state),
        performanceReportingEnabled: performanceReportingEnabled(state),
        cprPortfolioOverviewEnabled: cprPortfolioOverviewEnabled(state),
        insightsEnabled: insightsEnabled(state),
        powerbiReportAdmin: hasPowerbiReportAdmin(state),
        cprMediaAdmin: hasCprSharedMediaAdmin(state),
        aonTrustCompany: hasAonTrustCompany(state),
        hasPortfolioAccess: hasPortfolioAccess(state),
        clientPortfolioEnabled: hasClientPortfolioAccess(state),
        hasClientResearchEnabled: hasClientResearchEnabled(state),
    };
};

export interface IHeaderPropsFromActions {
    actions?: typeof HeaderActions;
}

export const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IHeaderPropsFromActions => {
    return {
        actions: bindActionCreators({...HeaderActions}, dispatch),
    };
};

const connectedComponent = connect<IHeaderPropsFromState, IHeaderPropsFromActions>
(mapStateToProps, mapDispatchToProps)(Header);

export default connectedComponent;
